import React, { CSSProperties, SelectHTMLAttributes, useMemo } from 'react';
import Select, { ActionMeta } from 'react-select';
import { CircleCounter } from './CircleCounter';
import { useTheme } from './hooks';
import { Dropdown } from 'bootstrap';
import Fuse from 'fuse.js';

export type MultiSelectProps<T extends unknown> = {
  title: string;
  options: T[];
  markedFirst?: boolean;
  isMarked: (option: T) => boolean;
  isDisabled?: (option: T) => boolean;
  onToggle: (option: T, checked: boolean) => void;
  optionRenderer: (option: T) => React.ReactNode;
  keyExtractor: (option: T) => string;
  allowAll?: boolean;
  onToggleAll?: (checked: boolean) => void;
};

export type MultiSelectSearchProps<T extends unknown> = {
  onChange: (
    value: Array<{ value: T; label: string }>,
    actionMeta: ActionMeta<T>,
  ) => void;
  menuPlacement?: 'top' | 'bottom' | 'auto';
  options: Array<{ value: T; label: string; searchValues?: string[] }>;
  value?: T[] | null;
};

export const selectStyles = {
  light: {},
  dark: {
    primary: 'var(--bs-body-color)',
    primary75: 'var(--bs-body-color)',
    primary25: 'var(--bs-secondary-bg)',
    neutral0: 'var(--bs-body-bg)',
    neutral10: 'var(--bs-secondary-bg)',
    neutral20: 'var(--bs-tertiary-bg)',
    neutral30: 'var(--bs-secondary-color)',
    neutral50: 'var(--bs-body-color)',
    neutral60: 'var(--bs-body-color)',
    neutral80: 'var(--bs-body-color)',
    neutral90: 'var(--bs-body-color)',
  },
};

export const MultiSelectButton = <T extends unknown>({
  title,
  options,
  markedFirst,
  keyExtractor,
  isMarked,
  isDisabled,
  onToggle,
  optionRenderer,
  className,
  style,
}: MultiSelectProps<T> & { style?: CSSProperties; className?: string }) => {
  const counter = options.filter(isMarked).length;
  const dropdownMenuRef = React.useRef<HTMLUListElement>(null);
  const dropdownToggleRef = React.useRef<HTMLButtonElement>(null);
  const dropdown = useMemo(() => {
    if (!dropdownMenuRef.current || !dropdownToggleRef.current) return null;
    return new Dropdown(dropdownMenuRef.current, {
      reference: dropdownToggleRef.current,
      autoClose: 'outside',
    });
  }, [dropdownMenuRef.current, dropdownToggleRef.current]);

  const toggle = () => {
    if (!dropdown) return;
    dropdown.toggle();
  };

  const onClickOutside = (event: MouseEvent) => {
    if (
      !dropdown ||
      dropdownMenuRef.current?.contains(event.target as Node) ||
      dropdownToggleRef.current?.contains(event.target as Node)
    ) {
      return;
    }
    dropdown.hide();
  };

  React.useEffect(() => {
    document.addEventListener('click', onClickOutside);
    return () => {
      document.removeEventListener('click', onClickOutside);
    };
  }, [dropdown]);

  return (
    <>
      <button
        ref={dropdownToggleRef}
        className={`btn btn-light dropdown-toggle justify-content-between ${className}`}
        role="button"
        style={style}
        onClick={toggle}
      >
        <span className="me-2">{title}</span>
        {counter > 0 && <CircleCounter count={counter} />}
      </button>
      <ul
        ref={dropdownMenuRef}
        style={{ maxHeight: '200px', overflowY: 'auto' }}
        className="dropdown-menu"
      >
        {options
          .slice()
          .sort((a, b) => {
            if (!markedFirst) {
              return 0;
            }
            const aValue = Number(isMarked(a));
            const bValue = Number(isMarked(b));
            return bValue - aValue;
          })
          .map((option) => {
            const disabled = isDisabled?.(option);
            return (
              <li
                key={keyExtractor(option)}
                className={`dropdown-item ${disabled ? 'disabled' : ''}`}
              >
                <div className="dropdown-item">
                  <div className="form-check">
                    <label
                      className={`filter-delivery-${keyExtractor(
                        option,
                      )}-check-label`}
                      htmlFor={`filter-delivery-${keyExtractor(option)}-check`}
                    >
                      {optionRenderer(option)}
                    </label>
                    <input
                      disabled={disabled}
                      className="form-check-input"
                      type="checkbox"
                      id={`filter-delivery-${keyExtractor(option)}-check`}
                      checked={isMarked(option)}
                      onChange={(e) => {
                        onToggle(option, e.target.checked);
                      }}
                    />
                  </div>
                </div>
              </li>
            );
          })}
      </ul>
    </>
  );
};

export const MultiSelectBox = <T extends unknown>({
  title,
  name,
  options,
  markedFirst,
  allowAll = false,
  labelWidth = 'auto',
  keyExtractor,
  isMarked,
  onToggle,
  onToggleAll,
  optionRenderer,
}: MultiSelectProps<T> & { name?: string; labelWidth?: string }) => {
  const counter = options.filter(isMarked).length;
  return (
    <>
      <div className="input-group">
        <span
          className="input-group-text"
          style={{ width: labelWidth }}
          id={`multiselect-box-${name}-label`}
        >
          <span className="me-2">{title}</span>
          {counter > 0 && <CircleCounter count={counter} />}
        </span>
        <ul
          id={`multiselect-box-${name}-dropdown`}
          style={{ maxHeight: '200px', overflowY: 'auto' }}
          className="dropdown-menu"
          aria-labelledby={`multiselect-box-${name}-input`}
        >
          {allowAll && (
            <li key="all" className="dropdown-item">
              <div className="form-check">
                <label
                  className={`filter-delivery-keyExtractor(option)-check-label`}
                  htmlFor={`filter-delivery-keyExtractor(option)-check`}
                >
                  All
                </label>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`filter-delivery-keyExtractor(option)-check`}
                  checked={counter === options.length && options.length > 0}
                  onChange={(e) => onToggleAll?.(e.target.checked)}
                />
              </div>
            </li>
          )}
          {options
            .slice()
            .sort((a, b) => {
              if (!markedFirst) {
                return 0;
              }
              const aValue = Number(isMarked(a));
              const bValue = Number(isMarked(b));
              return bValue - aValue;
            })
            .map((option) => (
              <li key={keyExtractor(option)} className="dropdown-item">
                <div className="form-check">
                  <label
                    className={`filter-delivery-keyExtractor(option)-check-label`}
                    htmlFor={`filter-delivery-keyExtractor(option)-check`}
                  >
                    {optionRenderer(option)}
                  </label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`filter-delivery-keyExtractor(option)-check`}
                    checked={isMarked(option)}
                    onChange={(e) => {
                      onToggle(option, e.target.checked);
                    }}
                  />
                </div>
              </li>
            ))}
        </ul>
        <a
          id={`multiselect-box-${name}-input`}
          className="form-select"
          style={{
            maxLines: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
          data-bs-auto-close="outside"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          href={`#multiselect-box-${name}-dropdown`}
        >
          {counter === options.length && options.length > 0
            ? 'All'
            : options.filter(isMarked).map((option, i) => {
                return (
                  <span key={keyExtractor(option)}>
                    {optionRenderer(option)}
                    {i < counter - 1 && ', '}
                  </span>
                );
              })}
        </a>
      </div>
    </>
  );
};

export const MultiSelectSearch = <T extends unknown>({
  onChange,
  options,
  menuPlacement,
  ...props
}: Omit<
  SelectHTMLAttributes<HTMLInputElement>,
  'onChange' | 'options' | 'value'
> &
  MultiSelectSearchProps<T>) => {
  const [appTheme] = useTheme();
  return (
    <Select
      {...props}
      // @ts-ignore
      value={props.value?.map((val) =>
        options.find((option) => option.value === val),
      )}
      menuPlacement={menuPlacement ?? 'auto'}
      defaultValue={[]}
      // @ts-ignore
      onChange={onChange}
      isMulti
      // @ts-ignore
      options={options}
      filterOption={(option, inputValue) => {
        if (!inputValue) {
          return true;
        }
        const fuse = new Fuse([option, option.data], {
          keys: ['searchValues', 'label'],
          includeScore: true,
          threshold: 0.2,
        });
        const results = fuse.search(inputValue);
        if (results.length > 0) {
          return true;
        }
        return false;
      }}
      theme={(theme) => ({
        ...theme,
        borderRadius: 4,
        colors: {
          ...theme.colors,
          ...selectStyles[appTheme],
        },
      })}
    />
  );
};
